import type { SurveyQuestion, SurveyResponseItem } from '@bandos/shared'

export type SurveyAnswer = string | number | boolean | string[] | null

export interface ReconciledQuestion extends SurveyQuestion {
  answers: SurveyAnswer[]
  isDeleted: boolean
}

/**
 * Resolve a single respondent's answer to a current question.
 *
 * Returns `null` when the question was added after this response was submitted
 * (id present on the current question but not in the response's snapshot).
 * Falls back to positional matching for legacy responses without a snapshot
 * or for snapshots whose questions lack stable ids.
 */
export function getAnswerForQuestion(
  response: SurveyResponseItem,
  question: SurveyQuestion,
  questionIndex: number
): SurveyAnswer {
  if (!response.questionsSnapshot || response.questionsSnapshot.length === 0) {
    return response.answers[questionIndex] ?? null
  }

  if (question.id) {
    const snapIndex = response.questionsSnapshot.findIndex((q) => q.id === question.id)
    if (snapIndex !== -1) {
      return response.answers[snapIndex] ?? null
    }
    return null
  }

  return response.answers[questionIndex] ?? null
}

/**
 * Merge current questions with all historical responses.
 *
 * Matching strategy per snapshot question (in order of preference):
 *   1. Stable ID match — survives reorders and edits, surfaces deletions as ghosts.
 *   2. Positional fallback — used when the snapshot question lacks an `id`
 *      (legacy responses, or responses saved before IDs were assigned).
 */
export function reconcileQuestions(
  currentQuestions: SurveyQuestion[],
  responses: SurveyResponseItem[]
): ReconciledQuestion[] {
  const currentBuckets = new Map<string, (string | number | boolean | string[] | null)[]>()
  currentQuestions.forEach((q, i) => {
    currentBuckets.set(q.id ?? `__pos_${i}`, [])
  })

  const deletedBuckets = new Map<
    string,
    { meta: SurveyQuestion; answers: (string | number | boolean | string[] | null)[] }
  >()
  const currentIdSet = new Set(currentQuestions.map((q) => q.id).filter(Boolean))

  for (const response of responses) {
    if (!response.questionsSnapshot || response.questionsSnapshot.length === 0) {
      // Pre-snapshot legacy responses — straight positional match against current questions
      currentQuestions.forEach((q, i) => {
        currentBuckets.get(q.id ?? `__pos_${i}`)?.push(response.answers[i] ?? null)
      })
      continue
    }

    response.questionsSnapshot.forEach((snapQ, i) => {
      const key = snapQ.id
      const answer = response.answers[i] ?? null

      if (key && currentBuckets.has(key)) {
        currentBuckets.get(key)?.push(answer)
      } else if (key && !currentIdSet.has(key)) {
        // ID was active at snapshot time but the question has since been deleted
        if (!deletedBuckets.has(key)) {
          deletedBuckets.set(key, { meta: snapQ, answers: [] })
        }
        deletedBuckets.get(key)?.answers.push(answer)
      } else if (i < currentQuestions.length) {
        // Snapshot lacks an ID — fall back to positional match against current questions
        const posKey = currentQuestions[i].id ?? `__pos_${i}`
        currentBuckets.get(posKey)?.push(answer)
      }
    })
  }

  const result: ReconciledQuestion[] = currentQuestions.map((q, i) => ({
    ...q,
    answers: currentBuckets.get(q.id ?? `__pos_${i}`) ?? [],
    isDeleted: false,
  }))

  for (const { meta, answers } of deletedBuckets.values()) {
    if (answers.filter((a) => a !== null).length > 0) {
      result.push({ ...meta, answers, isDeleted: true })
    }
  }

  return result
}
